import { memo, useState, useCallback } from 'react'
import {
  Send,
  MessageCircle,
  LogOut,
  Copy,
  Check,
  Loader2,
  Key,
  Lock,
} from 'lucide-react'
import { toast } from 'sonner'
import { useUsdcBalanceForAddress } from '@/hooks/useUsdcBalance'
import { usePortfolioValue } from '@/hooks/usePortfolioValue'
import { usePmWalletStore } from '@/store/pmWalletStore'
import { PinInput } from '@/components/PinInput'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface WalletDialogProps {
  open: boolean
  onClose: () => void
}

const TELEGRAM_BOT = import.meta.env.VITE_TELEGRAM_BOT_USERNAME as string | undefined

export const WalletDialog = memo(function WalletDialog({ open, onClose }: WalletDialogProps) {
  const pmAddress = usePmWalletStore((s) => s.pmAddress)
  const pmProxyAddress = usePmWalletStore((s) => s.pmProxyAddress)
  const encryptedData = usePmWalletStore((s) => s.encryptedData)
  const activeWallet = usePmWalletStore((s) => s.activeWallet)
  const setActiveWallet = usePmWalletStore((s) => s.setActiveWallet)
  const importKey = usePmWalletStore((s) => s.importKey)
  const unlock = usePmWalletStore((s) => s.unlock)
  const disconnectPm = usePmWalletStore((s) => s.disconnectPm)

  const balancePm = useUsdcBalanceForAddress(pmProxyAddress ?? undefined)
  const { value: portfolioPm } = usePortfolioValue(pmProxyAddress ?? undefined)

  const isPmConnected = activeWallet === 'pm' && pmAddress !== null
  const needsUnlock = activeWallet === 'pm' && encryptedData !== null && pmAddress === null

  const [showAddress, setShowAddress] = useState(false)
  const [copied, setCopied] = useState(false)
  const [keyInput, setKeyInput] = useState('')
  const [pinInput, setPinInput] = useState('')
  const [importing, setImporting] = useState(false)
  const [pinUnlockInput, setPinUnlockInput] = useState('')
  const [unlocking, setUnlocking] = useState(false)

  const handleCopy = useCallback(async (addr: string) => {
    if (!addr) return
    try {
      await navigator.clipboard.writeText(addr)
      setCopied(true)
      toast.success('Address copied')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }, [])

  const handleTelegram = useCallback(() => {
    if (!TELEGRAM_BOT) {
      toast.error('Telegram bot not configured')
      return
    }
    window.open(`https://t.me/${TELEGRAM_BOT}?start=fund`, '_blank')
  }, [])

  const handleImportKey = useCallback(async () => {
    const key = keyInput.trim()
    if (!key) { toast.error('Enter a private key'); return }
    if (pinInput.length < 4) { toast.error('PIN must be at least 4 digits'); return }
    setImporting(true)
    try {
      await importKey(key, pinInput)
      toast.success('Polymarket wallet connected')
      setKeyInput('')
      setPinInput('')
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invalid private key')
    } finally {
      setImporting(false)
    }
  }, [keyInput, pinInput, importKey, onClose])

  const handleUnlock = useCallback(async (pin?: string) => {
    const code = pin ?? pinUnlockInput
    if (code.length < 4) { toast.error('Enter PIN'); return }
    setUnlocking(true)
    try {
      const ok = await unlock(code)
      if (ok) {
        toast.success('Wallet unlocked')
        setPinUnlockInput('')
        onClose()
      } else {
        toast.error('Invalid PIN')
      }
    } finally {
      setUnlocking(false)
    }
  }, [pinUnlockInput, unlock, onClose])

  const handleDisconnectPm = useCallback(() => {
    disconnectPm()
    toast.success('PM wallet disconnected')
  }, [disconnectPm])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wallet-dialog-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close"
      />
      <div className="relative z-10 w-full max-w-sm rounded-xl border border-[#374151] bg-[#111827] p-5 shadow-xl max-h-[90dvh] overflow-y-auto">
        <div className="mb-5 flex items-center justify-between">
          <h2 id="wallet-dialog-title" className="text-lg font-semibold text-white">Wallet</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[#94a3b8] hover:bg-white/10 hover:text-white"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Unlock screen */}
        {needsUnlock && (
          <div className="space-y-4">
            <div className="text-xs font-medium uppercase tracking-wide text-[#94a3b8]">Unlock wallet</div>
            <div className="space-y-3 rounded-lg border border-[#374151] bg-[#0d1117] p-4">
              <p className="text-xs text-[#64748b]">Enter your PIN to access the wallet</p>
              <div className="flex justify-center">
                <PinInput
                  length={4}
                  autoFocus
                  onChange={setPinUnlockInput}
                  onComplete={(pin) => void handleUnlock(pin)}
                  disabled={unlocking}
                />
              </div>
              <Button
                variant="outline"
                onClick={() => void handleUnlock()}
                disabled={unlocking || pinUnlockInput.length < 4}
                className="w-full border-[#f59e0b]/30 bg-[#f59e0b]/10 text-[#f59e0b] hover:bg-[#f59e0b]/20"
              >
                {unlocking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                Unlock
              </Button>
            </div>
            <button
              type="button"
              onClick={handleDisconnectPm}
              className="text-xs text-[#94a3b8] hover:text-[#ef4444]"
            >
              Reset wallet
            </button>
          </div>
        )}

        {/* Connected */}
        {isPmConnected && (
          <>
            <div className="mb-4 rounded-lg bg-[#1f2937] px-4 py-3">
              <div className="mb-2 flex items-center gap-1.5">
                <div className="h-2 w-2 rounded-full bg-[#f59e0b]" />
                <span className="text-[10px] text-[#94a3b8] uppercase tracking-wide">Polymarket wallet</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div>
                  <div className="text-[10px] text-[#94a3b8] uppercase">Available</div>
                  <div className="text-lg font-bold text-white tabular-nums">
                    {balancePm != null ? balancePm.toFixed(2) : '0.00'} USDC
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-[#94a3b8] uppercase">Portfolio</div>
                  <div className="text-lg font-semibold text-white tabular-nums">
                    {(portfolioPm != null || balancePm != null)
                      ? `${((portfolioPm ?? 0) + (balancePm ?? 0)).toFixed(2)} USDC`
                      : '—'}
                  </div>
                </div>
              </div>
            </div>

            {/* Deposit */}
            <div className="mb-4 space-y-2">
              <div className="text-xs font-medium uppercase tracking-wide text-[#94a3b8]">Deposit</div>
              {pmProxyAddress && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowAddress((v) => !v)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors',
                      'bg-[#1f2937] hover:bg-[#374151]',
                    )}
                  >
                    <Send className="h-5 w-5 shrink-0 text-[#00ff6a]" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-white">Polymarket wallet</div>
                      <div className="text-xs text-[#94a3b8]">USDC or MATIC / Polygon</div>
                    </div>
                  </button>
                  {showAddress && (
                    <div className="rounded-lg border border-[#374151] bg-[#0d1117] px-4 py-3">
                      <div className="mb-2 break-all font-mono text-xs text-[#e2e8f0]">{pmProxyAddress}</div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-[#94a3b8]">Network: Polygon</span>
                        <button
                          type="button"
                          onClick={() => handleCopy(pmProxyAddress)}
                          className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-[#00ff6a] transition-colors hover:bg-[#00ff6a]/10"
                        >
                          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                          {copied ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
              <button
                type="button"
                onClick={handleTelegram}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors',
                  'bg-[#1f2937] hover:bg-[#374151]',
                )}
              >
                <MessageCircle className="h-5 w-5 shrink-0 text-[#38bdf8]" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white">Top up via Telegram</div>
                  <div className="text-xs text-[#94a3b8]">Buy crypto in the bot</div>
                </div>
              </button>
            </div>

            <div className="flex justify-start">
              <button
                type="button"
                onClick={handleDisconnectPm}
                className="flex items-center gap-1 text-xs text-[#94a3b8] transition-colors hover:text-[#ef4444]"
              >
                <LogOut className="h-3.5 w-3.5" />
                Log out
              </button>
            </div>
          </>
        )}

        {/* PM import key form */}
        {!isPmConnected && !needsUnlock && (
          <div className="space-y-4">
            <div className="text-xs font-medium uppercase tracking-wide text-[#94a3b8]">Polymarket</div>
            <div className="space-y-3 rounded-lg border border-[#374151] bg-[#0d1117] p-3">
              <p className="text-[11px] text-[#94a3b8]">
                Export your private key at{' '}
                <a
                  href="https://reveal.magic.link/polymarket"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#f59e0b] underline"
                >
                  reveal.magic.link/polymarket
                </a>
                {' '}and paste it below.
              </p>
              <div>
                <label className="mb-1 block text-xs text-[#94a3b8]">Private key</label>
                <input
                  type="password"
                  placeholder="0x..."
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                  className="w-full rounded-lg border border-[#374151] bg-[#1f2937] px-3 py-2 font-mono text-xs text-white placeholder:text-[#64748b] focus:border-[#f59e0b] focus:outline-none"
                  disabled={importing}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs text-[#94a3b8]">Create PIN (4 digits)</label>
                <div className="flex justify-center">
                  <PinInput length={4} onChange={setPinInput} disabled={importing} />
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { void handleImportKey(); setActiveWallet('pm') }}
                disabled={importing || !keyInput.trim() || pinInput.length < 4}
                className="w-full border-[#f59e0b]/30 bg-[#f59e0b]/10 text-[#f59e0b] hover:bg-[#f59e0b]/20"
              >
                {importing ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Importing...</>
                ) : (
                  <><Key className="mr-2 h-4 w-4" />Connect</>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
})
